import { Button } from 'primereact/button';
import React, { useRef, useState } from 'react';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import { Toast } from 'primereact/toast';
import { useFormContext, useWatch } from 'react-hook-form';
import { ApprovalSequenceFormFields } from '../../../Interfaces/admin/ApprovalSequence.interface';
import DropdownStyled from '../../../components/inputs/StyledInputs/DropdownStyled';
import FormErrorMessage from '../../../components/messages/FormErrorMessage';
import LoadingButton from '../../../components/inputs/LoadingButton';
import { useQuery } from '@tanstack/react-query';
import {
  getApprovalSequences,
  getApprovalSequencesCopyMembers,
} from '../../../services/admin/ApprovalSequences';
import { formatReportLabel } from '@/src/utils/roleUtils';

type CopyASButtonProps = {
  disableOptionID?: string;
};

const CopyASButton = ({ disableOptionID }: CopyASButtonProps) => {
  const { setValue, clearErrors } =
    useFormContext<ApprovalSequenceFormFields>();
  const [report, company] = useWatch<ApprovalSequenceFormFields>({
    name: ['report', 'company'],
  });
  const [selectedApprovalSequence, setSelectedApprovalSequence] =
    useState<string>();
  const [showDialog, setShowDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const toast = useRef<Toast>(null);

  const hide = () => {
    setShowDialog(false);
    setShowError(false);
    setSelectedApprovalSequence(undefined);
  };

  const { data, isFetching, isError } = useQuery({
    queryKey: ['getApprovalSequencesCopy'],
    queryFn: ({ signal }) => {
      return getApprovalSequences(
        {
          page: 0,
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
  });

  const copyRequest = useQuery({
    queryKey: ['getContractData', selectedApprovalSequence, company, report],
    queryFn: ({ signal }) =>
      getApprovalSequencesCopyMembers(
        selectedApprovalSequence ?? '',
        { companyID: company as string, report: report as string },
        signal
      ),
    onSuccess: (data) => {
      clearErrors();

      if (!data.length) {
        setValue('sequence', [{ step: 1, members: [] }]);
        hide();
        return;
      }

      const processedData = data.reduce(
        (acc, member) => {
          const step = member.step;
          const record = acc.members[step];
          const newMemberData = {
            id: member.assignee
              ? `${member.access.id}--${member.assignee.id}`
              : member.access.id,
            name: member.assignee
              ? member.assignee.name
              : member.access.role ?? member.access.user ?? '',
            assigneeId: member.assignee?.id,
          };

          if (record) {
            record.members.push(newMemberData);
          } else {
            acc.members[step] = {
              step,
              members: [newMemberData],
            };
            acc.latestStep = acc.latestStep < step ? step : acc.latestStep;
          }

          return acc;
        },
        { members: {}, latestStep: 0 } as {
          members: Record<number, ApprovalSequenceFormFields['sequence'][0]>;
          latestStep: number;
        }
      );

      for (let i = 1; i < processedData.latestStep; i++) {
        if (!processedData.members[i]) {
          processedData.members[i] = { step: i, members: [] };
        }
      }

      const sequence = Object.values(processedData.members).sort(
        (a, b) => a.step - b.step
      );
      setValue('sequence', sequence);
      hide();
    },
    onError: () => {
      showToast(
        'error',
        toast,
        'Copy Approval Sequence',
        'An error occurred! please try again later',
        3000
      );
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <ConfirmationDialog
        tagKey="approval-sequence-copy"
        visibility={showDialog}
        Button={
          <Button
            className="w-21rem buttonSecondary border-0 flex py-3"
            onClick={() => {
              setShowDialog(true);
            }}
            disabled={copyRequest.isFetching}
          >
            <span className="mx-auto text-22px text-white">
              Copy Approval Sequence
            </span>
          </Button>
        }
        contentClassName="pb-0"
        icon="hidden"
        acceptClassName="hidden"
        rejectClassName="hidden"
        onHide={() => hide()}
        message={
          <div className="mr-3">
            <div className="mb-1">
              Are you sure you want to overwrite the current sequence?
            </div>
            <div>
              <label htmlFor="contract-dp" className="text-standard block mb-2">
                Please select the approval sequence to copy:
              </label>
              <DropdownStyled
                id={'contract-dp'}
                error={showError}
                onChange={(e) => {
                  const value = e.value;

                  if (value) {
                    setShowError(false);
                  }

                  setSelectedApprovalSequence(value);
                }}
                value={selectedApprovalSequence}
                options={
                  data?.sequences.map((sequence) => ({
                    id: sequence.id,
                    Label: `${sequence.company.name} - ${formatReportLabel(
                      sequence.report
                    )}`,
                    disabled: sequence.id === disableOptionID,
                  })) ?? []
                }
                labelField="Label"
                valueField="id"
                placeholder={
                  isError
                    ? 'Failed to load sequences!'
                    : 'Select Approval Sequence'
                }
                isDisabled={isError || !company || !report}
                tooltip={
                  !company || !report
                    ? 'To enable the sequence copy selector please fill out Company and Report.'
                    : undefined
                }
                tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                isLoading={isFetching}
                clearable={true}
                filter={true}
                resetFilterOnHide={true}
              />
              {showError && (
                <FormErrorMessage errorMessage="Approval Sequence is required" />
              )}
            </div>
            <div className="flex justify-content-end gap-2 mt-5 pb-2">
              <Button
                onClick={() => {
                  hide();
                }}
                className="blackText font-bold transparent border-none hover:bg-gray-200 active:bg-gray-200 active:text-black-alpha-90 hover:text-black-alpha-90"
              >
                No
              </Button>
              <LoadingButton
                className="bluwaiBlue font-bold border-none mr-2"
                onClick={() => {
                  if (!selectedApprovalSequence) {
                    setShowError(true);
                  } else {
                    copyRequest.refetch();
                  }
                }}
                label="Yes"
                isLoading={copyRequest.isFetching}
                fontSize="text-base"
              />
            </div>
          </div>
        }
      />
    </React.Fragment>
  );
};

export default CopyASButton;
